// imports
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { useState } from 'react';

// local imports
import { RootState, uiActions } from '../../store';
import { PrimaryButton } from '../../App/SidePanel/PrimaryButton';
import CustomDialog from '../../App/CustomDialog';
import { USGS_BLUE } from '../../constants/colors';

// Styled components
const SectionContainer = styled(Box)({
  border: '1px solid #e0e0e0',
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: 16,
});

const SectionHeader = styled(Box)({
  backgroundColor: '#f5f5f5',
  padding: 12,
  borderBottom: '1px solid #e0e0e0',
});

const SectionHeaderText = styled(Typography)({
  fontSize: '1rem',
});

const CheckboxContainer = styled(Box)({
  padding: 12,
});

const CheckboxGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 4,
});

const CheckboxItemWithIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const StyledHelpIcon = styled(HelpOutlineIcon)({
  fontSize: '1rem',
  color: USGS_BLUE,
  marginLeft: '-10px',
});

const RadioContainer = styled(Box)({
  display: 'flex',
  marginBottom: 16,
  gap: 32,
  marginLeft: '15px',
});

const RadioSection = styled(Box)({
  flex: 1,
});

const RadioSectionWide = styled(Box)({
  flex: 1.8,
});

const StyledDivider = styled(Divider)({
  marginLeft: 8,
  marginRight: 8,
});

const StyledCheckbox = styled(Checkbox)({
  '&.Mui-checked': {
    color: USGS_BLUE,
  },
});

const StyledRadio = styled(Radio)({
  '&.Mui-checked': {
    color: USGS_BLUE,
  },
});

const ContentWrapper = styled(Box)({
  minWidth: 450,
});

/**
 * Map Download Dialog
 *
 * Renders a modal with map download options.
 *
 * @returns {JSX.Element} The rendered Map Download Dialog component.
 */
export default function MapDownloadDialog() {
  const { mapDownloadDialogOpen } = useSelector((state: RootState) => state.ui);
  const dispatch = useDispatch();

  // Set default download options
  // Checkboxes
  const [qrCode, setQrCode] = useState(false);
  const [dataSourceInfo, setDataSourceInfo] = useState(false);

  // Radio buttons
  const [format, setFormat] = useState('PNG');
  const [view, setView] = useState('CONUS');

  const handleClose = () => {
    dispatch(uiActions.toggleMapDownloadDialog());
  };

  const handleDownload = () => {
    // Placeholder for later
    // TODO: delete the console.log and replace with real data download
    console.log('Download clicked', {
      qrCode,
      dataSourceInfo,
      format,
      view,
    });
  };

  const downloadButton = (
    <PrimaryButton
      variant="contained"
      startIcon={<DownloadIcon />}
      onClick={handleDownload}
      fullWidth
      size="medium"
    >
      Download
    </PrimaryButton>
  );

  return (
    <CustomDialog
      open={mapDownloadDialogOpen}
      onClose={handleClose}
      title="Download Map"
      dataTestId="map-download-dialog"
      actions={downloadButton}
    >
      <ContentWrapper>
        <SectionContainer>
          <SectionHeader>
            <SectionHeaderText variant="h6">
              Include in Map Download
            </SectionHeaderText>
          </SectionHeader>
          <CheckboxContainer>
            <CheckboxGrid>
              <CheckboxItemWithIcon>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={qrCode}
                      onChange={(e) => setQrCode(e.target.checked)}
                      size="small"
                    />
                  }
                  label="QR code"
                />
                <StyledHelpIcon />
              </CheckboxItemWithIcon>
              <CheckboxItemWithIcon>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={dataSourceInfo}
                      onChange={(e) => setDataSourceInfo(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Data source info"
                />
                <StyledHelpIcon />
              </CheckboxItemWithIcon>
            </CheckboxGrid>
          </CheckboxContainer>
        </SectionContainer>
        <RadioContainer>
          <RadioSection>
            <Typography variant="subtitle1">Format</Typography>
            <RadioGroup
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <FormControlLabel
                value="PNG"
                control={<StyledRadio size="small" />}
                label="PNG"
              />
              <FormControlLabel
                value="PDF"
                control={<StyledRadio size="small" />}
                label="PDF"
              />
            </RadioGroup>
          </RadioSection>
          <StyledDivider orientation="vertical" flexItem />
          <RadioSectionWide>
            <Typography variant="subtitle1">View</Typography>
            <RadioGroup value={view} onChange={(e) => setView(e.target.value)}>
              <FormControlLabel
                value="CONUS"
                label="CONUS"
                control={<StyledRadio size="small" />}
              />
              <FormControlLabel
                value="Current Zoom"
                control={<StyledRadio size="small" />}
                label="Current Zoom"
              />
            </RadioGroup>
          </RadioSectionWide>
        </RadioContainer>
      </ContentWrapper>
    </CustomDialog>
  );
}
